import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// Los umbrales viven en el backend (routes/network-health.js) y llegan calculados en el
// campo `estado` de cada sede. Acá solo se usan para pintar celdas sueltas; el criterio
// de "degradado" nunca se decide en el frontend, para que la tabla, el dashboard y el
// gate del botón de IA no puedan discrepar.
const UMBRAL = { gw_ms: 5, wan_ms: 250, jitter_ms: 30, loss_pct: 2, dns_ms: 300, util_pct: 70 };

function Metric({ value, umbral, suffix = ' ms', decimals = 1 }) {
  if (value == null) return <span style={{ color: 'var(--gray-500)' }}>—</span>;
  const n = Number(value);
  const malo = n > umbral;
  return (
    <span style={{ color: malo ? 'var(--red)' : 'var(--ink-2)', fontWeight: malo ? 600 : 400, fontSize: 12 }}>
      {n.toFixed(decimals)}{suffix}
    </span>
  );
}

const ESTADO = {
  ok:         { label: 'OK',         color: 'var(--ink-2)' },
  degradado:  { label: 'Degradada',  color: 'var(--red)' },
  sin_datos:  { label: 'Sin datos',  color: 'var(--gray-500)' },
};

function EstadoTag({ estado }) {
  const e = ESTADO[estado] || ESTADO.sin_datos;
  return <span style={{ color: e.color, fontSize: 11, fontWeight: 600 }}>{e.label}</span>;
}

export default function NetworkHealthPage() {
  const [sites, setSites] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [summary, setSummary] = useState([]);
  const [assets, setAssets] = useState([]);
  const [view, setView] = useState('sedes');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [macs, setMacs] = useState({});          // ediciones de MAC en curso, por id/cidr
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [apData, setApData] = useState({ aps: [], bssids: [] });
  const [asignar, setAsignar] = useState({});    // por BSSID: { ap: id | '__nuevo', nombre }
  const [renombrar, setRenombrar] = useState({}); // por id de AP: nombre en edición

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Un fetch que falla NO puede verse igual que "no hay datos": un 500 en candidatos
  // mostraba "No hay subredes nuevas para proponer" y escondió un error de SQL en
  // producción. Cada pedido reporta su propio problema.
  const pedir = async (path, setter, etiqueta, errores) => {
    try {
      const r = await fetch(`${API_URL}/network-health/${path}`, { headers });
      if (!r.ok) {
        errores.push(r.status === 403
          ? `${etiqueta}: no tenés permisos para verlo (requiere admin o técnico)`
          : `${etiqueta}: el servidor respondió ${r.status}`);
        return;
      }
      const j = await r.json();
      setter(Array.isArray(j) ? j : []);
    } catch (e) {
      errores.push(`${etiqueta}: ${e.message}`);
    }
  };

  const load = async () => {
    setLoading(true);
    const errores = [];
    await Promise.all([
      pedir('sites', setSites, 'Redes habilitadas', errores),
      pedir('sites/candidates', setCandidates, 'Subredes detectadas', errores),
      pedir('summary', setSummary, 'Resumen por sede', errores),
      pedir('assets', setAssets, 'Mediciones por equipo', errores),
      (async () => {
        try {
          const r = await fetch(`${API_URL}/network-health/access-points`, { headers });
          if (!r.ok) { errores.push(`Puntos de acceso: el servidor respondió ${r.status}`); return; }
          const j = await r.json();
          setApData({ aps: j.aps || [], bssids: j.bssids || [] });
        } catch (e) {
          errores.push(`Puntos de acceso: ${e.message}`);
        }
      })(),
    ]);
    setErrors(errores);
    setLoading(false);
  };

  useEffect(load, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const hayDegradadas = summary.some(s => s.estado === 'degradado');

  const analizar = async () => {
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const r = await fetch(`${API_URL}/network-health/analyze`, { method: 'POST', headers });
      const j = await r.json();
      setAnalysis(r.ok ? (j.analysis || j.message) : (j.error || `El servidor respondió ${r.status}`));
    } catch (e) {
      setAnalysis('No se pudo generar el análisis: ' + e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const habilitar = async (c) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/network-health/sites`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: c.location_name, cidr: c.cidr, location_id: c.location_id,
          gateway_mac: macs[c.cidr] || undefined,
        }),
      });
      setMacs(m => ({ ...m, [c.cidr]: '' }));
      load();
    } finally { setBusy(false); }
  };

  const guardarMac = async (s) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/network-health/sites/${s.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ gateway_mac: (macs[s.id] || '').trim() || null }),
      });
      setMacs(m => ({ ...m, [s.id]: undefined }));
      load();
    } finally { setBusy(false); }
  };

  const quitar = async (s) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/network-health/sites/${s.id}`, { method: 'DELETE', headers });
      load();
    } finally { setBusy(false); }
  };

  // Puntos de acceso. Cada acción reporta su propio error: un 403 o 500 no puede verse
  // como "no pasó nada".
  const apFetch = async (path, method, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/network-health/access-points${path}`, {
        method, headers: { ...headers, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErrors([j.error || (r.status === 403
          ? 'No tenés permisos para editar puntos de acceso (requiere admin o técnico)'
          : `El servidor respondió ${r.status}`)]);
        return false;
      }
      return true;
    } finally { setBusy(false); }
  };

  const asignarBssid = async (b) => {
    const sel = asignar[b.bssid] || { ap: b.sugerido_ap_id || '' };
    let ok;
    if (sel.ap === '__nuevo') {
      if (!(sel.nombre || '').trim()) return;
      ok = await apFetch('', 'POST', { name: sel.nombre.trim(), site_id: b.site_id, bssids: [b.bssid] });
    } else if (sel.ap) {
      ok = await apFetch(`/bssids/${b.bssid}`, 'PUT', { ap_id: sel.ap });
    }
    if (ok) { setAsignar(m => ({ ...m, [b.bssid]: undefined })); load(); }
  };

  const desasignarBssid = async (bssid) => {
    if (await apFetch(`/bssids/${bssid}`, 'DELETE')) load();
  };

  const guardarNombreAp = async (ap) => {
    if (await apFetch(`/${ap.id}`, 'PATCH', { name: renombrar[ap.id] })) {
      setRenombrar(m => ({ ...m, [ap.id]: undefined }));
      load();
    }
  };

  const borrarAp = async (ap) => {
    if (!window.confirm(`¿Eliminar el punto de acceso "${ap.name}"? Sus BSSID vuelven a quedar sin nombre.`)) return;
    if (await apFetch(`/${ap.id}`, 'DELETE')) load();
  };

  const sinNombre = apData.bssids.filter(b => !b.ap_id).length;

  // Equipos agrupados por ubicación. Dentro de cada grupo van de peor a mejor (el backend
  // ya los manda ordenados por `calidad`), y los grupos también: primero la ubicación que
  // contiene el peor equipo, que es por donde uno quiere empezar a mirar.
  const grupos = (() => {
    const m = new Map();
    for (const a of assets) {
      const k = a.location_name || 'Sin ubicación';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    }
    return [...m.entries()]
      .map(([nombre, equipos]) => ({
        nombre, equipos,
        peor: Math.max(...equipos.map(e => Number(e.calidad) || 0)),
      }))
      .sort((a, b) => b.peor - a.peor || a.nombre.localeCompare(b.nombre));
  })();

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Salud de red</div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            Latencia, pérdida, jitter y uso del enlace medidos por los agentes dentro de las sedes — promedios de las últimas 6 h
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={load} disabled={loading}>
            {loading ? 'Cargando...' : 'Actualizar'}
          </button>
          <button className="btn btn-primary" onClick={analizar} disabled={!hayDegradadas || analyzing}
            title={hayDegradadas
              ? 'Analiza con IA las mediciones fuera de parámetro'
              : 'Se habilita cuando hay mediciones fuera de parámetro'}>
            {analyzing ? 'Analizando...' : 'Analizar con IA'}
          </button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: 16, borderLeft: '3px solid var(--red)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--red)', marginBottom: 4 }}>
            No se pudo cargar parte de la información
          </div>
          {errors.map((e, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--ink-2)' }}>{e}</div>
          ))}
        </div>
      )}

      {/* El análisis va ARRIBA de la tabla: es la lectura de los datos que siguen. */}
      {(analyzing || analysis) && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>Análisis de la red</div>
            {analysis && (
              <button className="btn btn-secondary" onClick={() => setAnalysis(null)}
                style={{ padding: '2px 8px', fontSize: 11 }}>Cerrar</button>
            )}
          </div>
          {analyzing
            ? <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Analizando las mediciones de las últimas 6 horas...</div>
            : <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{analysis}</div>}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--gray-100)' }}>
          {[
            { key: 'sedes',   label: 'Por sede' },
            { key: 'equipos', label: 'Por equipo' },
            { key: 'aps',     label: `Puntos de acceso (${apData.aps.length})${sinNombre ? ` · ${sinNombre} sin nombre` : ''}` },
            { key: 'config',  label: `Redes habilitadas (${sites.length})` },
          ].map(v => (
            <button key={v.key} className={`btn ${view === v.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView(v.key)}>{v.label}</button>
          ))}
        </div>

        {loading && <div style={{ padding: 24, fontSize: 13, color: 'var(--gray-500)' }}>Cargando...</div>}

        {/* ── Resumen por sede (últimas 6 h) ── */}
        {!loading && view === 'sedes' && (
          summary.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--gray-500)' }}>
              No hay redes habilitadas todavía. Cargalas en "Redes habilitadas" — hasta entonces
              los agentes no miden nada.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Sede</th><th>Estado</th><th>Equipos</th><th>Muestras</th>
                  <th>Gateway p50</th><th>Gateway p95</th>
                  <th>WAN p50</th><th>WAN p95</th><th>Jitter</th>
                  <th>Pérdida</th><th>DNS p50</th>
                </tr>
              </thead>
              <tbody>
                {[...summary]
                  .sort((a, b) => (b.motivos?.length || 0) - (a.motivos?.length || 0) || a.name.localeCompare(b.name))
                  .map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)', fontFamily: 'monospace' }}>{s.cidr}</div>
                    </td>
                    <td>
                      <EstadoTag estado={s.estado} />
                      {s.motivos?.length > 0 && (
                        <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{s.motivos.join(' · ')}</div>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>{s.equipos || 0}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{s.muestras || 0}</td>
                    <td><Metric value={s.gw_p50} umbral={UMBRAL.gw_ms} decimals={2} /></td>
                    <td><Metric value={s.gw_p95} umbral={UMBRAL.gw_ms} decimals={2} /></td>
                    <td><Metric value={s.wan_p50} umbral={UMBRAL.wan_ms} /></td>
                    <td><Metric value={s.wan_p95} umbral={UMBRAL.wan_ms} /></td>
                    <td><Metric value={s.wan_jitter} umbral={UMBRAL.jitter_ms} /></td>
                    <td title={s.muestras_con_perdida > 0
                          ? `${s.muestras_con_perdida} de ${s.muestras} muestras con algún paquete perdido`
                          : 'sin paquetes perdidos en la ventana'}>
                      <Metric value={Math.max(s.gw_loss_avg || 0, s.wan_loss_avg || 0)}
                              umbral={UMBRAL.loss_pct} suffix=" %" decimals={2} />
                    </td>
                    <td><Metric value={s.dns_p50} umbral={UMBRAL.dns_ms} decimals={0} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* ── Equipos agrupados por ubicación, de peor a mejor ── */}
        {!loading && view === 'equipos' && (
          grupos.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--gray-500)' }}>
              Sin mediciones. Requiere agente .NET 2.7.0 y al menos una red habilitada.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Equipo</th><th>Enlace</th>
                  <th>Gateway</th><th>WAN</th><th>Jitter</th><th>Pérdida</th>
                  <th>DNS</th><th>Uso RX</th><th>WiFi</th><th>Últ. muestra</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map(g => (
                  <React.Fragment key={g.nombre}>
                    <tr>
                      <td colSpan={10} style={{
                        background: 'var(--bg-subtle)', fontSize: 12, fontWeight: 700,
                        color: 'var(--ink)', padding: '8px 12px',
                      }}>
                        {g.nombre}
                        <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>
                          {'  '}· {g.equipos.length} equipo{g.equipos.length === 1 ? '' : 's'}
                        </span>
                        {g.peor > 0 && (
                          <span style={{ color: 'var(--red)', fontWeight: 600 }}>{'  '}· fuera de parámetro</span>
                        )}
                      </td>
                    </tr>
                    {g.equipos.map(a => (
                      <tr key={a.id}>
                        <td>
                          <Link to={`/assets/${a.asset_id}`} style={{ color: 'var(--accent)' }}>{a.hostname}</Link>
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                          {a.adapter_type === 'wifi' ? 'WiFi' : 'Cable'}
                          {a.link_speed_mbps ? ` · ${a.link_speed_mbps} Mbps` : ''}
                        </td>
                        <td><Metric value={a.gw_avg_ms} umbral={UMBRAL.gw_ms} decimals={2} /></td>
                        <td><Metric value={a.wan_avg_ms} umbral={UMBRAL.wan_ms} /></td>
                        <td><Metric value={a.wan_jitter_ms} umbral={UMBRAL.jitter_ms} /></td>
                        <td title={a.muestras_con_perdida > 0
                              ? `${a.muestras_con_perdida} de ${a.muestras} muestras con algún paquete perdido`
                              : 'sin paquetes perdidos en 6 h'}>
                          <Metric value={Math.max(a.gw_loss_pct || 0, a.wan_loss_pct || 0)}
                                  umbral={UMBRAL.loss_pct} suffix=" %" decimals={2} />
                        </td>
                        <td><Metric value={a.dns_ms} umbral={UMBRAL.dns_ms} decimals={0} /></td>
                        <td><Metric value={a.rx_util_pct} umbral={UMBRAL.util_pct} suffix=" %" decimals={1} /></td>
                        <td style={{ fontSize: 11 }}>
                          {a.adapter_type === 'wifi' && a.wifi_ssid ? (
                            <>
                              <div title={a.wifi_bssid ? `BSSID ${a.wifi_bssid}` : 'El agente no informó el BSSID (requiere 2.21.1 en Windows 11)'}>
                                {a.wifi_ap_name
                                  ? <strong>{a.wifi_ap_name}</strong>
                                  : <span style={{ color: 'var(--gray-500)' }}>{a.wifi_bssid ? 'AP sin nombre' : 'AP desconocido'}</span>}
                              </div>
                              <div style={{ color: 'var(--gray-500)' }}>
                                {a.wifi_ssid}{a.wifi_signal_pct != null ? ` · ${a.wifi_signal_pct}%` : ''}
                              </div>
                            </>
                          ) : '—'}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--gray-500)' }}
                            title={`${a.muestras} muestras en 6 h`}>
                          {a.sampled_at ? new Date(a.sampled_at).toLocaleString('es-AR',
                            { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* ── Puntos de acceso WiFi ── */}
        {!loading && view === 'aps' && (
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
              Los agentes informan el <strong>BSSID</strong> (la MAC de la radio) del AP al que está conectado
              cada equipo. Poniéndole nombre una vez, la vista "Por equipo" muestra el AP. Un mismo AP tiene
              varios BSSID (uno por banda y por red WiFi): asignalos todos al mismo punto de acceso.
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
              BSSID detectados — últimos 7 días
            </div>
            {apData.bssids.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 20 }}>
                Todavía no hay equipos WiFi informando el BSSID (requiere agente 2.21.1 y estar en una red habilitada).
              </div>
            ) : (
              <table className="table" style={{ marginBottom: 20 }}>
                <thead>
                  <tr><th>BSSID</th><th>Red</th><th>Sede</th><th>Equipos</th><th>Punto de acceso</th><th></th></tr>
                </thead>
                <tbody>
                  {apData.bssids.map(b => {
                    const sel = asignar[b.bssid] || { ap: b.sugerido_ap_id || '' };
                    const set = patch => setAsignar(m => ({ ...m, [b.bssid]: { ...sel, ...patch } }));
                    return (
                      <tr key={b.bssid}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.bssid}</td>
                        <td style={{ fontSize: 12 }}>{b.ssid || '—'}</td>
                        <td style={{ fontSize: 12 }}>{b.site_name || 'Sin sede'}</td>
                        <td style={{ fontSize: 12 }}
                            title={`Última conexión: ${new Date(b.ultimo).toLocaleString('es-AR')}`}>
                          {b.equipos}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {b.ap_id ? <strong>{b.ap_name}</strong> : (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <select className="form-input" value={sel.ap}
                                onChange={e => set({ ap: e.target.value })}
                                style={{ padding: '4px 8px', fontSize: 12, width: 190 }}>
                                <option value="">Elegir punto de acceso…</option>
                                {apData.aps.map(ap => (
                                  <option key={ap.id} value={ap.id}>
                                    {ap.name}{ap.site_name ? ` (${ap.site_name})` : ''}
                                  </option>
                                ))}
                                <option value="__nuevo">+ Nuevo punto de acceso…</option>
                              </select>
                              {sel.ap === '__nuevo' && (
                                <input className="form-input" autoFocus placeholder="Ej: Depósito planta alta"
                                  value={sel.nombre || ''} onChange={e => set({ nombre: e.target.value })}
                                  onKeyDown={e => e.key === 'Enter' && asignarBssid(b)}
                                  style={{ padding: '4px 8px', fontSize: 12, width: 170 }} />
                              )}
                              {b.sugerido_ap_id && sel.ap === b.sugerido_ap_id && (
                                <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>sugerido</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {b.ap_id ? (
                            <button className="btn btn-secondary" disabled={busy}
                              title="Quitarle el nombre a este BSSID"
                              onClick={() => desasignarBssid(b.bssid)}>Quitar</button>
                          ) : (
                            <button className="btn btn-primary" onClick={() => asignarBssid(b)}
                              disabled={busy || !sel.ap || (sel.ap === '__nuevo' && !(sel.nombre || '').trim())}>
                              Asignar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {apData.aps.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
                  Puntos de acceso con nombre
                </div>
                <table className="table">
                  <thead><tr><th>Nombre</th><th>Sede</th><th>BSSID</th><th></th></tr></thead>
                  <tbody>
                    {apData.aps.map(ap => {
                      const editando = renombrar[ap.id] !== undefined;
                      return (
                        <tr key={ap.id}>
                          <td style={{ fontSize: 13, fontWeight: 600 }}>
                            {editando ? (
                              <input className="form-input" autoFocus value={renombrar[ap.id]}
                                onChange={e => setRenombrar(m => ({ ...m, [ap.id]: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && guardarNombreAp(ap)}
                                style={{ padding: '4px 8px', fontSize: 12, width: 200 }} />
                            ) : ap.name}
                          </td>
                          <td style={{ fontSize: 12 }}>{ap.site_name || '—'}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ink-2)' }}>
                            {ap.bssids.length ? ap.bssids.map(x => <div key={x}>{x}</div>) : '—'}
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {editando ? (
                              <>
                                <button className="btn btn-primary" disabled={busy || !(renombrar[ap.id] || '').trim()}
                                  onClick={() => guardarNombreAp(ap)} style={{ marginRight: 6 }}>Guardar</button>
                                <button className="btn btn-secondary" disabled={busy}
                                  onClick={() => setRenombrar(m => ({ ...m, [ap.id]: undefined }))}>Cancelar</button>
                              </>
                            ) : (
                              <>
                                <button className="btn btn-secondary" disabled={busy}
                                  onClick={() => setRenombrar(m => ({ ...m, [ap.id]: ap.name }))}
                                  style={{ marginRight: 6 }}>Renombrar</button>
                                <button className="btn btn-secondary" disabled={busy} onClick={() => borrarAp(ap)}>Eliminar</button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {/* ── Redes habilitadas ── */}
        {!loading && view === 'config' && (
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
              Los agentes miden <strong>solamente</strong> cuando su subred figura acá. En cualquier
              otra red (por ejemplo la casa de un usuario) no miden ni reportan la huella de la red.
              La MAC del gateway se fija sola en la primera medición; cargala a mano cuando dos sedes
              comparten la misma subred, para que no se la quede la que reporte primero.
            </div>

            {sites.length > 0 && (
              <table className="table" style={{ marginBottom: 20 }}>
                <thead>
                  <tr><th>Nombre</th><th>Subred</th><th>MAC del gateway</th><th>Ubicación</th><th>Muestras 24 h</th><th></th></tr>
                </thead>
                <tbody>
                  {sites.map(s => {
                    const editando = macs[s.id] !== undefined;
                    return (
                      <tr key={s.id}>
                        <td style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.cidr}</td>
                        <td>
                          {editando ? (
                            <input className="form-input" value={macs[s.id]} autoFocus
                              placeholder="AA-BB-CC-11-22-33"
                              onChange={e => setMacs(m => ({ ...m, [s.id]: e.target.value }))}
                              style={{ width: 160, padding: '4px 8px', fontSize: 12, fontFamily: 'monospace' }} />
                          ) : (
                            <span style={{ fontFamily: 'monospace', fontSize: 11,
                                           color: s.gateway_mac ? 'var(--ink-2)' : 'var(--gray-500)' }}>
                              {s.gateway_mac || 'sin fijar'}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 12 }}>{s.location_name || '—'}</td>
                        <td style={{ fontSize: 12 }}>{s.muestras_24h || 0}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {editando ? (
                            <>
                              <button className="btn btn-primary" disabled={busy} onClick={() => guardarMac(s)}
                                style={{ marginRight: 6 }}>Guardar</button>
                              <button className="btn btn-secondary" disabled={busy}
                                onClick={() => setMacs(m => ({ ...m, [s.id]: undefined }))}>Cancelar</button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-secondary" disabled={busy}
                                title="Fijar o corregir la MAC del gateway de esta sede"
                                onClick={() => setMacs(m => ({ ...m, [s.id]: s.gateway_mac || '' }))}
                                style={{ marginRight: 6 }}>MAC</button>
                              <button className="btn btn-secondary" disabled={busy} onClick={() => quitar(s)}>Quitar</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
              Subredes detectadas en el inventario
            </div>
            {candidates.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                No hay subredes nuevas para proponer.
              </div>
            ) : (
              <table className="table">
                <thead><tr><th>Subred</th><th>Ubicación</th><th>Equipos</th><th>MAC del gateway (opcional)</th><th></th></tr></thead>
                <tbody>
                  {candidates.map(c => (
                    <tr key={c.cidr}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.cidr}</td>
                      <td style={{ fontSize: 12 }}>
                        {c.location_name}
                        {Number(c.sedes) > 1 && (
                          <div style={{ fontSize: 10, color: '#D45D00' }}>
                            la misma subred aparece en {c.sedes} ubicaciones — conviene fijar la MAC
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{c.equipos}</td>
                      <td>
                        <input className="form-input" value={macs[c.cidr] || ''}
                          placeholder="AA-BB-CC-11-22-33"
                          onChange={e => setMacs(m => ({ ...m, [c.cidr]: e.target.value }))}
                          style={{ width: 160, padding: '4px 8px', fontSize: 12, fontFamily: 'monospace' }} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-secondary" disabled={busy} onClick={() => habilitar(c)}>
                          Habilitar medición
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
